// pages/public/HomePage.jsx
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import PublicLayout from '../../layout/PublicLayout';
import ProductCard from '../../components/public/ProductCard';
import { getProductosPublicos, getRestauranteActual } from '../../api/public';
import { useTypewriterColored } from '../../hooks/useTypewriterColored';
import { ChevronRight, Star, Clock, Award } from 'lucide-react';

const HomePage = () => {
  const navigate = useNavigate();
  const [productosDestacados, setProductosDestacados] = useState([]);
  const [loading, setLoading] = useState(true);
  const [whatsappNumber, setWhatsappNumber] = useState('');
  
  // Efecto typewriter para toda la frase
  const { part1, part2 } = useTypewriterColored('Sabor que Conquista', 10, 100, 50, 2000);

  useEffect(() => {
    const loadProductos = async () => {
      try {
        const productos = await getProductosPublicos();
        // Mostrar los primeros 6 productos
        setProductosDestacados(productos.slice(0, 6));

        // Cargar datos del restaurante para obtener el WhatsApp
        const restauranteData = await getRestauranteActual();
        if (restauranteData && restauranteData.telefono) {
          setWhatsappNumber(restauranteData.telefono);
        }
      } catch (error) {
        console.error('Error cargando productos:', error);
      } finally {
        setLoading(false);
      }
    };
    loadProductos();
  }, []);

  return (
    <PublicLayout>
      {/* Hero Section - Compacto y de alto impacto */}
      <section className="relative bg-gradient-to-r from-red-600 via-orange-600 to-amber-600 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-8 pb-12 sm:pt-16 sm:pb-20 md:pt-24 md:pb-28">
          <div className="grid md:grid-cols-2 gap-6 sm:gap-8 items-center">
            <div>
              <h1 className="text-2xl sm:text-4xl md:text-5xl lg:text-6xl font-extrabold mb-2.5 sm:mb-6 leading-tight">
                <span className="text-white">{part1}</span>
                <span className="text-yellow-300">
                  {part2}
                  <span className="animate-pulse">|</span>
                </span>
              </h1>
              <p className="text-xs sm:text-base md:text-lg mb-4 sm:mb-8 text-red-50 max-w-xl leading-relaxed">
                Descubre nuestra deliciosa variedad de platos preparados con ingredientes frescos 
                y el amor de siempre.
              </p>
              <div className="flex flex-row gap-2.5 sm:gap-4 items-center">
                <button
                  onClick={() => navigate('/menu')}
                  className="bg-white text-red-600 px-4 sm:px-8 py-2.5 sm:py-3.5 rounded-full font-bold text-xs sm:text-base hover:bg-yellow-300 hover:text-red-700 transition-all shadow-md hover:shadow-lg flex items-center gap-1.5 cursor-pointer shrink-0"
                >
                  <span>Ver Menú</span>
                  <ChevronRight className="w-4 h-4" />
                </button>
                <button
                  onClick={() => navigate('/promociones')}
                  className="bg-transparent border border-white sm:border-2 text-white px-4 sm:px-8 py-2.5 sm:py-3.5 rounded-full font-bold text-xs sm:text-base hover:bg-white hover:text-red-600 transition-all cursor-pointer shrink-0"
                >
                  Promociones
                </button>
              </div>
            </div>
            
            {/* Imagen de Plato - Desktop */}
            <div className="hidden md:flex justify-center">
              <div className="relative">
                <div className="absolute inset-0 bg-yellow-300 rounded-full blur-3xl opacity-30"></div>
                <div className="relative">
                  <img 
                    src="https://images.unsplash.com/photo-1598103442097-8b74394b95c6?w=500&h=500&fit=crop" 
                    alt="Pollo Asado Ecuatoriano" 
                    className="w-80 h-80 lg:w-96 lg:h-96 object-cover rounded-full shadow-2xl animate-float"
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
        
        {/* Wave decoration - Curva fluida original sin líneas ni cortes en móvil */}
        <div className="absolute -bottom-1 -left-[1px] -right-[1px] pointer-events-none leading-none z-10">
          <svg 
            viewBox="0 0 1440 120" 
            fill="none" 
            xmlns="http://www.w3.org/2000/svg" 
            className="w-full h-auto block overflow-visible"
          >
            <path 
              d="M-2 120L60 105C120 90 240 60 360 45C480 30 600 30 720 37.5C840 45 960 60 1080 67.5C1200 75 1320 75 1380 75L1442 75V135H-2Z" 
              fill="white"
            />
          </svg>
        </div>
      </section>

      {/* Features Section - 3 Columnas en Móvil para Ahorrar Espacio Vertical */}
      <section className="py-6 sm:py-14 bg-white">
        <div className="max-w-7xl mx-auto px-3 sm:px-6 lg:px-8">
          <div className="grid grid-cols-3 gap-2 sm:gap-6">
            <div className="text-center p-2 sm:p-4 rounded-xl hover:bg-red-50/50 transition-colors">
              <div className="bg-red-50 w-11 h-11 sm:w-16 sm:h-16 rounded-full flex items-center justify-center mx-auto mb-2 border border-red-100/60">
                <Clock className="w-5 h-5 sm:w-8 sm:h-8 text-red-600" />
              </div>
              <h3 className="text-xs sm:text-base font-bold text-gray-900 mb-0.5">Entrega Rápida</h3>
              <p className="text-[11px] sm:text-sm text-gray-500 hidden sm:block">
                Tu pedido listo en tiempo récord sin perder calidad
              </p>
            </div>

            <div className="text-center p-2 sm:p-4 rounded-xl hover:bg-red-50/50 transition-colors">
              <div className="bg-red-50 w-11 h-11 sm:w-16 sm:h-16 rounded-full flex items-center justify-center mx-auto mb-2 border border-red-100/60">
                <Star className="w-5 h-5 sm:w-8 sm:h-8 text-red-600" />
              </div>
              <h3 className="text-xs sm:text-base font-bold text-gray-900 mb-0.5">Calidad Premium</h3>
              <p className="text-[11px] sm:text-sm text-gray-500 hidden sm:block">
                Ingredientes frescos y el auténtico sazón tradicional
              </p>
            </div>

            <div className="text-center p-2 sm:p-4 rounded-xl hover:bg-red-50/50 transition-colors">
              <div className="bg-red-50 w-11 h-11 sm:w-16 sm:h-16 rounded-full flex items-center justify-center mx-auto mb-2 border border-red-100/60">
                <Award className="w-5 h-5 sm:w-8 sm:h-8 text-red-600" />
              </div>
              <h3 className="text-xs sm:text-base font-bold text-gray-900 mb-0.5">Servicio 10/10</h3>
              <p className="text-[11px] sm:text-sm text-gray-500 hidden sm:block">
                Atención cálida y personalizada en cada visita
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Productos Destacados - 2 Columnas en Móvil */}
      <section className="py-8 sm:py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-3 sm:px-6 lg:px-8">
          <div className="text-center mb-6 sm:mb-12">
            <h2 className="text-2xl sm:text-4xl font-extrabold text-gray-900 mb-1 sm:mb-3">
              Nuestros <span className="text-red-600">Favoritos</span>
            </h2>
            <p className="text-xs sm:text-lg text-gray-600">
              Los platos más populares que nuestros clientes aman
            </p>
          </div>

          {loading ? (
            <div className="text-center py-12">
              <div className="inline-block animate-spin rounded-full h-10 w-10 border-b-2 border-red-600"></div>
              <p className="mt-3 text-gray-600 text-sm">Cargando productos...</p>
            </div>
          ) : (
            <>
              {/* Grid Responsivo: 2 Columnas en Móvil, 3 en Tablet, 3 en Desktop */}
              <div className="grid grid-cols-2 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-3 gap-3 sm:gap-6">
                {productosDestacados.map((producto) => (
                  <ProductCard 
                    key={producto.id_producto} 
                    producto={producto} 
                    whatsappNumber={whatsappNumber}
                  />
                ))}
              </div>

              <div className="text-center mt-6 sm:mt-12">
                <button
                  onClick={() => navigate('/menu')}
                  className="bg-red-600 hover:bg-red-700 text-white px-5 sm:px-8 py-2.5 sm:py-3.5 rounded-full font-bold text-xs sm:text-base transition-all shadow-md hover:shadow-lg inline-flex items-center gap-1.5 cursor-pointer"
                >
                  <span>Ver Menú Completo</span>
                  <ChevronRight className="w-4 h-4" />
                </button>
              </div>
            </>
          )}
        </div>
      </section>

      {/* CTA Section - Compacta */}
      <section className="py-10 sm:py-16 bg-gradient-to-r from-red-600 via-orange-600 to-amber-600 text-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-2xl sm:text-4xl font-extrabold mb-2 sm:mb-4">
            ¿Listo para saborear lo mejor?
          </h2>
          <p className="text-xs sm:text-base mb-6 text-red-50">
            Encuentra el restaurante más cercano y haz tu pedido ahora
          </p>
          <button
            onClick={() => navigate('/restaurantes')}
            className="bg-white text-red-600 px-6 sm:px-10 py-2.5 sm:py-3.5 rounded-full font-bold text-xs sm:text-base hover:bg-yellow-300 hover:text-red-700 transition-all shadow-md hover:shadow-lg cursor-pointer"
          >
            Encontrar Restaurante
          </button>
        </div>
      </section>
    </PublicLayout>
  );
};

export default HomePage;
